import React, { useState, useEffect, useRef } from 'react';
import { sendEmail, SendEmailRequest, EmailAttachment } from '../api/emails';
import { Claim, fetchClaimDocument, sendMultipleRepEmail, sendDeceasedIntroEmail, client as apiClient } from '../api/claims';
import ComplexAIButton from './ComplexAIButton';
import './EmailComposer.css';

// Force version update
const EMAIL_COMPOSER_VERSION = '2024-01-24-v3-' + Date.now();
console.log('📧 EmailComposer loaded - Version:', EMAIL_COMPOSER_VERSION);

interface EmailComposerProps {
  claim?: Claim;
  legacyClaimId?: string;
  taskId?: string;  // Email task ID for reply tracking
  defaultTo?: string;
  defaultSubject?: string;
  defaultBody?: string;
  initialAttachments?: EmailAttachment[];
  /** The address the original email came FROM (e.g. the lender), if this
   * composer was opened as a "forward"/"reply" to a specific message. When
   * set, sending TO this same address triggers a confirm -- callers like
   * "Forward to client" pre-address the client, but a mis-clicked template
   * or manual edit could otherwise silently send back to the lender. */
  warnIfRecipient?: string;
  onClose: () => void;
  onSent?: (result: any) => void;
}

// CODE VERSION: 2024-01-24-v2 - Using backend proxy for document fetching
export default function EmailComposer({
  claim,
  legacyClaimId,
  taskId,
  defaultTo = '',
  defaultSubject = '',
  defaultBody = '',
  initialAttachments = [],
  warnIfRecipient,
  onClose,
  onSent
}: EmailComposerProps) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [attachments, setAttachments] = useState<EmailAttachment[]>(() => initialAttachments);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    console.log('🚀 EmailComposer mounted - Version 2024-01-24-v3');
    console.log('🚀 fetchClaimDocument function:', typeof fetchClaimDocument);
    console.log('🚀 fetchClaimDocument imported from claims.ts:', fetchClaimDocument);
  }, []);

  useEffect(() => {
    // If we have a claim, pre-populate some fields
    if (claim) {
      if (!defaultTo && claim.craFinanceLender) {
        // You might want to look up the lender email here
        setSubject(`RE: Claim ${claim.proclaimReference || claim.id}`);
      }
    } else if (legacyClaimId && !defaultSubject) {
      // For legacy claims, use the legacy claim ID in the subject
      setSubject(`RE: Legacy Claim ${legacyClaimId}`);
    }
  }, [claim, legacyClaimId, defaultTo, defaultSubject]);


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result?.toString().split(',')[1] || '';
        setAttachments(prev => [...prev, {
          name: file.name,
          content: base64
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const [showTemplateConfirm, setShowTemplateConfirm] = useState(false);
  const [templateResult, setTemplateResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleTemplateSelect = (templateKey: string) => {
    setSelectedTemplate(templateKey);
    if (!templateKey) {
      // Reset fields when template is cleared
      setSubject(defaultSubject);
      setBody(defaultBody);
      return;
    }

    if (templateKey === 'deceased-intro' && claim) {
      const lf = claim.litigationFriendDetails;
      const repName = `${lf?.firstName || ''} ${lf?.lastName || ''}`.trim();
      const deceasedName = `${claim.firstName || ''} ${claim.lastName || ''}`.trim() || 'the deceased';
      const claimRef = claim.proclaimReference || claim.id || '';

      setTo(lf?.email || '');
      setSubject(`Your car finance commission claim - ${claimRef}`);
      // Read-only preview of the MailerSend template (v69oxl53mnxg785k) that
      // will actually be sent server-side; the body isn't edited/sent from here.
      setBody(
`Claim Number: ${claimRef}

Dear ${repName || '[estate representative]'},

Thank you for instructing Complex Law to assist you, in your capacity as representative of the estate of the late ${deceasedName}, to pursue a car finance commission claim.

In order for us to act on your behalf and progress this matter, we kindly ask that you provide the following documents at your earliest convenience:

Death Certificate;
Will (if one exists);
Grant of Probate (if a will exists) or Letters of Administration (if no will exists), both documents can be obtained from the Probate Registry;
Copy of your ID (i.e. passport or driving licence).

Without the above documentation we are unable to progress the claim(s) and we would have no option but to close these matter(s).

Alternatively, if you decide you do not want to continue and wish to close the file(s) entirely, kindly provide a copy of the death certificate and we will process the cancellation without charge.

Should you have any questions about the above please do not hesitate to contact us.

Regards,`
      );
      return;
    }

    if (templateKey === 'multiple-rep' && claim) {
      const clientName = `${claim.firstName || ''} ${claim.lastName || ''}`.trim() || 'Client';
      const lenderName = claim.craFinanceLender || 'your lender';
      const claimRef = claim.proclaimReference || claim.id || '';
      // "Lender Reference" precedence mirrors the backend's
      // multi_rep_agreement_number: the lender's own reference when we hold
      // one, else the signup agreement number, else N/A. (Legacy records
      // store the literal string "NULL".)
      const rawAgreement = String(
        (claim as any).lenderAgreementNumber || claim.craAgreementNumberOriginal || ''
      ).trim();
      const agreementNumber =
        !rawAgreement || ['NULL', 'NONE'].includes(rawAgreement.toUpperCase())
          ? 'N/A'
          : rawAgreement;

      setTo(claim.email || '');
      // Mirrors the send route's subject — the template tells the client the
      // reference is "located in the subject of this email".
      setSubject(`Regarding your claim - ${claimRef}`);

      // DISPLAY-ONLY preview of MailerSend template jpzkmgq5e1ng059v
      // (21 Aug 2026 wording). The actual send goes through
      // POST /send-multiple-rep-email, which renders the real template
      // server-side — keep this text in step with it.
      const multiRepPreview = (lenderEmail: string) =>
`Our Reference: ${claimRef}

Lender Reference: ${agreementNumber}

Dear ${clientName},

We confirm we have received correspondence from your financial lender ${lenderName}, advising that you, the client, have brought this complaint via more than one legal representative.

You can only have one legal representative acting on your behalf in the same matter.

If you wish for any other solicitor/ legal representative, who are not Complex Law, to remain acting, we shall proceed to close our file of papers. Please be aware there is a £180 plus VAT cancellation fee.

Can you please confirm within 7 days from the date of this email if you wish for Complex Law solicitors to close all claims or whether you will be cancelling all other legal representatives instructed in this matter?

However, if you wish to continue with Complex Law as your sole representative in this matter, you must inform ${lenderName} of this.

To do this, please email ${lenderName} via ${lenderEmail} within 7 days from the date of this email advising Complex Law are your sole legal representative and include our claim reference number (located in the subject of this email). Please, cc my email address into your email so we can update our records that your lender has received your instruction.

If this request is not completed within the 7 days from the date of this email, this will be deemed a breach of the retainer, and we will have no option but to cancel all claims due to a lack of cooperation.

Should your claims be closed due to a breach of retainer/lack of co-operation you will be responsible for our legal fees. This fee will be capped at £180 plus VAT (£216), per claim, if paid within 14 days of closure. Amount may increase if not paid within this time.

We look forward to your urgent response.

Kind regards,`;

      // Fetch the mapped lender email for the preview. The send route
      // resolves it server-side too and refuses with a clear error if the
      // lender has no mapped address.
      apiClient.get<{ lenderEmail: string; lenderName: string }>(`/ui/claims/${claim.id}/lender-email`)
        .then(res => {
          setBody(multiRepPreview(
            res.data?.lenderEmail
              || '[no lender email mapped — fix the lender mapping before sending]'
          ));
        })
        .catch(() => {
          setBody(multiRepPreview(
            '[no lender email mapped — fix the lender mapping before sending]'
          ));
        });
    }
  };

  const handleConfirmTemplateSend = async () => {
    setShowTemplateConfirm(false);
    if (!claim) return;

    if (selectedTemplate === 'multiple-rep') {
      try {
        setSending(true);
        setError(null);
        await sendMultipleRepEmail(claim.id);
        onSent?.({ message: 'Multiple Rep Email sent successfully' });
        onClose();
      } catch (err: any) {
        setError(`Failed to send: ${err.response?.data?.error || err.message}`);
        setSending(false);
      }
    } else if (selectedTemplate === 'deceased-intro') {
      try {
        setSending(true);
        setError(null);
        await sendDeceasedIntroEmail(claim.id);
        onSent?.({ message: 'Deceased Client Intro Email sent successfully' });
        onClose();
      } catch (err: any) {
        setError(`Failed to send: ${err.response?.data?.error || err.message}`);
        setSending(false);
      }
    }
  };

  const attachClaimDocument = async (docType: 'foa' | 'dba' | 'loc', displayName: string) => {
    console.log('📎 EmailComposer.attachClaimDocument called - Version 2024-01-24-v3');
    console.log('📎 Document type:', docType, 'Display name:', displayName);
    console.log('📎 Using fetchClaimDocument from api/claims.ts');
    
    if (!claim) {
      // For legacy claims, we can't attach documents this way yet
      if (legacyClaimId) {
        setError(`Document attachment for legacy claims is not yet supported`);
      }
      return;
    }
    
    try {
      setError(null);
      console.log('📎 Calling fetchClaimDocument from claims API...');
      // Use the API to fetch the document
      const { content, filename } = await fetchClaimDocument(claim.id, docType);
      console.log('📎 Document fetched successfully:', filename);
      
      // Check if already attached
      if (attachments.some(att => att.name === filename)) {
        return;
      }
      
      // Add to attachments
      setAttachments(prev => [...prev, {
        name: filename,
        content: content
      }]);
    } catch (err) {
      console.error(`Error attaching ${displayName}:`, err);
      setError(`Failed to attach ${displayName} document. Please try again.`);
    }
  };

  const handleSend = async () => {
    // If a template is selected, use the dedicated endpoint instead
    if (selectedTemplate && claim) {
      setShowTemplateConfirm(true);
      return;
    }

    if (!to) {
      setError('Please enter at least one recipient');
      return;
    }

    if (!subject) {
      setError('Please enter a subject');
      return;
    }

    if (warnIfRecipient && warnIfRecipient.trim()) {
      const warnAddr = warnIfRecipient.trim().toLowerCase();
      const recipients = to.split(',').map(e => e.trim().toLowerCase()).filter(e => e);
      if (recipients.includes(warnAddr)) {
        const proceed = window.confirm(
          'This is the address the email came FROM (usually the lender). Send anyway?'
        );
        if (!proceed) return;
      }
    }

    setSending(true);
    setError(null);

    try {
      const trimmedBody = body?.trim() ?? '';
      const bodyLooksLikeHtml = /<[^>]+>/.test(trimmedBody);
      const normalizedBody = bodyLooksLikeHtml
        ? body
        : body.replace(/\n/g, '<br />');

      const emailData: SendEmailRequest = {
        to: to.split(',').map(e => e.trim()).filter(e => e),
        cc: cc ? cc.split(',').map(e => e.trim()).filter(e => e) : undefined,
        bcc: bcc ? bcc.split(',').map(e => e.trim()).filter(e => e) : undefined,
        subject,
        body: normalizedBody,
        isHtml: true,
        attachments,
        claimId: claim?.id,
        legacyClaimId: legacyClaimId,
        taskId: taskId,  // Include task ID for reply tracking
        saveToSent: true
      };

      const result = await sendEmail(emailData);
      
      if (onSent) {
        onSent(result);
      }
      
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const insertAtCursor = (text: string) => {
    const textarea = bodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = body.substring(0, start) + text + body.substring(end);
    setBody(newValue);
    
    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
      textarea.focus();
    }, 0);
  };

  const handleFormat = (format: string) => {
    const textarea = bodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = body.substring(start, end);

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          formattedText = `<a href="${url}">${selectedText || url}</a>`;
        }
        break;
    }

    if (formattedText) {
      const newValue = body.substring(0, start) + formattedText + body.substring(end);
      setBody(newValue);
    }
  };

  try {
    return (
      <div className="email-composer-overlay">
        <div className="email-composer">
          <div className="email-composer-header">
            <h2>Compose Email {legacyClaimId ? `(Legacy Claim: ${legacyClaimId})` : ''}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="email-composer-body">
          <div className="email-field">
            <label>To:</label>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="Enter email addresses separated by commas"
              disabled={sending}
            />
          </div>

          <div className="email-field">
            <label>CC:</label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="Enter CC addresses separated by commas"
              disabled={sending}
            />
          </div>

          <div className="email-field">
            <label>BCC:</label>
            <input
              type="text"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="Enter BCC addresses separated by commas"
              disabled={sending}
            />
          </div>

          {claim && (
            <div className="email-field">
              <label>Template:</label>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                disabled={sending}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  backgroundColor: '#fff',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Select a template --</option>
                <option value="multiple-rep">Multiple Rep Email</option>
                {/* Deceased-only: role 'litigationFriend' = living client lacking
                    capacity, so a litigation friend on file is NOT evidence of
                    death. Mirrors the server-side gate on the send route. */}
                {(claim.clientDeceased === true ||
                  claim.litigationFriendDetails?.role === 'executor' ||
                  claim.litigationFriendDetails?.role === 'administrator') && (
                  <option value="deceased-intro">Deceased Client Intro Email</option>
                )}
              </select>
            </div>
          )}

          <div className="email-field">
            <label>Subject:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
              disabled={sending}
            />
          </div>

          <div className="email-field body-field">
            <label>Message:</label>
            <div className="format-toolbar">
              <button 
                type="button" 
                onClick={() => handleFormat('bold')}
                disabled={sending}
                title="Bold"
              >
                <strong>B</strong>
              </button>
              <button 
                type="button" 
                onClick={() => handleFormat('italic')}
                disabled={sending}
                title="Italic"
              >
                <em>I</em>
              </button>
              <button 
                type="button" 
                onClick={() => handleFormat('underline')}
                disabled={sending}
                title="Underline"
              >
                <u>U</u>
              </button>
              <button 
                type="button" 
                onClick={() => handleFormat('link')}
                disabled={sending}
                title="Insert Link"
              >
                🔗
              </button>
              {(claim || legacyClaimId) && (
                <button
                  type="button"
                  onClick={() => {
                    if (claim) {
                      insertAtCursor(`Our Reference: ${claim.proclaimReference || claim.id}`);
                    } else if (legacyClaimId) {
                      insertAtCursor(`Our Reference: ${legacyClaimId}`);
                    }
                  }}
                  disabled={sending}
                  title="Insert Our Reference"
                >
                  Our Ref
                </button>
              )}
              {claim?.craAgreementNumberOriginal && (
                <button
                  type="button"
                  onClick={() => {
                    insertAtCursor(`Lender Reference: ${claim.craAgreementNumberOriginal}`);
                  }}
                  disabled={sending}
                  title="Insert Lender Reference (Agreement Number)"
                >
                  Lender Ref
                </button>
              )}
              {(claim?.firstName || claim?.lastName) && (
                <button
                  type="button"
                  onClick={() => {
                    insertAtCursor(`Client Name: ${`${claim.firstName || ''} ${claim.lastName || ''}`.trim()}`);
                  }}
                  disabled={sending}
                  title="Insert Client Name"
                >
                  Client Name
                </button>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <ComplexAIButton
                  draftType="email"
                  claim={claim}
                  existingBody={body}
                  disabled={sending}
                  onInsert={(draftText) => insertAtCursor(draftText)}
                  onReplace={(draftText) => setBody(draftText)}
                />
              </div>
            </div>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message here..."
              rows={10}
              disabled={sending}
            />
          </div>

          <div className="email-field attachments-field">
            <label>Attachments:</label>
            
            {/* Claim Documents Quick Attach - Only for regular claims */}
            {claim && !legacyClaimId && (
              <div className="claim-documents-attach" style={{ marginBottom: '10px' }}>
                <span style={{ fontSize: '14px', color: '#666', marginRight: '10px' }}>Quick attach:</span>
                {claim.formOfAuthorityUrl && (
                  <button
                    type="button"
                    onClick={() => attachClaimDocument('foa', 'FOA')}
                    className="quick-attach-btn"
                    disabled={sending || attachments.some(att => att.name.toLowerCase().includes('authority'))}
                    style={{
                      padding: '4px 8px',
                      marginRight: '5px',
                      fontSize: '12px',
                      backgroundColor: attachments.some(att => att.name.toLowerCase().includes('authority')) ? '#28a745' : '#f8f9fa',
                      color: attachments.some(att => att.name.toLowerCase().includes('authority')) ? 'white' : '#333',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {attachments.some(att => att.name.toLowerCase().includes('authority')) ? '✓ FOA' : 'FOA'}
                  </button>
                )}
                {claim.damageBasedAgreementUrl && (
                  <button
                    type="button"
                    onClick={() => attachClaimDocument('dba', 'DBA')}
                    className="quick-attach-btn"
                    disabled={sending || attachments.some(att => att.name.toLowerCase().includes('damage') || att.name.toLowerCase().includes('dba'))}
                    style={{
                      padding: '4px 8px',
                      marginRight: '5px',
                      fontSize: '12px',
                      backgroundColor: attachments.some(att => att.name.toLowerCase().includes('damage') || att.name.toLowerCase().includes('dba')) ? '#28a745' : '#f8f9fa',
                      color: attachments.some(att => att.name.toLowerCase().includes('damage') || att.name.toLowerCase().includes('dba')) ? 'white' : '#333',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {attachments.some(att => att.name.toLowerCase().includes('damage') || att.name.toLowerCase().includes('dba')) ? '✓ DBA' : 'DBA'}
                  </button>
                )}
                {claim.locDocUrl && (
                  <button
                    type="button"
                    onClick={() => attachClaimDocument('loc', 'LOC')}
                    className="quick-attach-btn"
                    disabled={sending || attachments.some(att => att.name.toLowerCase().includes('letter') || att.name.toLowerCase().includes('loc'))}
                    style={{
                      padding: '4px 8px',
                      marginRight: '5px',
                      fontSize: '12px',
                      backgroundColor: attachments.some(att => att.name.toLowerCase().includes('letter') || att.name.toLowerCase().includes('loc')) ? '#28a745' : '#f8f9fa',
                      color: attachments.some(att => att.name.toLowerCase().includes('letter') || att.name.toLowerCase().includes('loc')) ? 'white' : '#333',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {attachments.some(att => att.name.toLowerCase().includes('letter') || att.name.toLowerCase().includes('loc')) ? '✓ LOC' : 'LOC'}
                  </button>
                )}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              disabled={sending}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="attach-button"
              disabled={sending}
            >
              📎 Attach Files
            </button>
            {attachments.length > 0 && (
              <div className="attachments-list">
                {attachments.map((att, index) => (
                  <div key={index} className="attachment-item">
                    <span>{att.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      disabled={sending}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="email-composer-footer">
          <button
            onClick={handleSend}
            disabled={sending}
            className="send-button"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
          <button
            onClick={onClose}
            disabled={sending}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Template Confirmation Modal */}
      {showTemplateConfirm && claim && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
            maxWidth: '440px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 600 }}>
              {selectedTemplate === 'deceased-intro' ? 'Send Deceased Client Intro Email' : 'Send Multiple Representation Email'}
            </h3>
            {selectedTemplate === 'deceased-intro' ? (
              <p style={{ color: '#475569', fontSize: '14px', margin: '0 0 20px' }}>
                This will send the estate document request to the representative{' '}
                <strong>{`${claim.litigationFriendDetails?.firstName || ''} ${claim.litigationFriendDetails?.lastName || ''}`.trim() || '(no name on file)'}</strong>{' '}
                ({claim.litigationFriendDetails?.email || 'no email on file'}), regarding the late{' '}
                <strong>{`${claim.firstName || ''} ${claim.lastName || ''}`.trim()}</strong>.
              </p>
            ) : (
              <>
                <p style={{ color: '#475569', fontSize: '14px', margin: '0 0 8px' }}>
                  This will send a clarification email to <strong>{`${claim.firstName || ''} ${claim.lastName || ''}`.trim()}</strong> ({claim.email}) regarding <strong>{claim.craFinanceLender || 'their lender'}</strong>.
                </p>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 20px' }}>
                  A 7-day follow-up task will be created automatically.
                </p>
              </>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowTemplateConfirm(false); setSelectedTemplate(''); }}
                style={{
                  padding: '8px 20px', borderRadius: '8px', border: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc', color: '#475569', cursor: 'pointer', fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmTemplateSend}
                style={{
                  padding: '8px 20px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: '14px'
                }}
              >
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
  } catch (error) {
    console.error('EmailComposer render error:', error);
    return <div>Error rendering email composer: {String(error)}</div>;
  }
}
